import type { Transition, Workflow } from './types'
import { SELECTION_COLOR } from './constants'
import { getTransitionPath, getConnectionPoints, getArrowheadPoints, getTransitionMidpoint } from './geometry'

interface Props {
  transition: Transition
  workflow: Workflow
  isSelected: boolean
  theme: 'dark' | 'light'
}

export default function TransitionLine({ transition, workflow, isSelected, theme }: Props) {
  const pathD = getTransitionPath(transition, workflow)
  if (!pathD) return null

  const from = workflow.activities.find(a => a.id === transition.from)
  const to = workflow.activities.find(a => a.id === transition.to)
  if (!from || !to) return null

  const lineColor = isSelected ? SELECTION_COLOR : (theme === 'light' ? '#000000' : '#ffffff')
  const lineWidth = isSelected ? 3 : 2

  const { startX, startY, endX, endY } = getConnectionPoints(from, to)
  const arrowPoints = getArrowheadPoints(endX, endY, startX, startY)

  // Condition text
  const mid = getTransitionMidpoint(transition, workflow)

  return (
    <g>
      {/* Line path */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={lineWidth}
      />

      {/* Invisible wider path for easier click targeting */}
      <path
        d={pathD}
        fill="none"
        stroke="transparent"
        strokeWidth={14}
        style={{ cursor: 'pointer' }}
      />

      {/* Arrowhead */}
      <polygon
        points={arrowPoints}
        fill={lineColor}
      />

      {/* Condition text */}
      {transition.condition && (
        <g>
          <rect
            x={mid.x - (transition.condition.length * 3.5) - 4}
            y={mid.y - 10}
            width={transition.condition.length * 7 + 8}
            height={18}
            fill={theme === 'light' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'}
            rx={2}
          />
          <text
            x={mid.x}
            y={mid.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill={theme === 'light' ? '#000000' : '#ffffff'}
            fontSize={12}
            fontFamily="Arial, sans-serif"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {transition.condition}
          </text>
        </g>
      )}
    </g>
  )
}
